import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import {
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
  NavigationEnd,
} from '@angular/router';
import { MatListItem, MatNavList } from '@angular/material/list';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { UsersService } from '../../services/users/users.service';
import { LoginService } from '../../services/login/login.service';
import { StorageService } from '../../services/storage/storage.service';
import { Subscription } from 'rxjs';
import { MenuRefreshService } from '../../services/menu/menu-refresh.service';
import { environment } from 'src/environments/environment';
import { TokenExpirationService } from '../../services/token-expiration/token-expiration.service';
export interface IMenuOption {
  icon?: string;
  not_svg?: boolean;
  title: string;
  link: string;
  identifier?: string;
  isExpandable?: boolean;
  expanded?: boolean;
  subsections?: { title: string; link: string; identifier?: string }[];
}

export interface ISubMenu {
  name: string;
  options: IMenuOption[];
  state?: any; // <- estat passat amb els links del menú
}

interface Permission {
  functionality: string;
  can_read: boolean;
  can_write: boolean;
  can_delete: boolean;
}

interface UserRole {
  id: number;
  name: string;
  description: string;
  permissions: Permission[];
}

interface UserProfile {
  username: string;
  email: string;
  is_admin: boolean;
  token: string;
  user_id: number;
  employee_id: number;
  external_id: string;
  serveto_user_info?: any;
  serveto_user_roles?: UserRole[];
}

@Component({
  selector: 'app-sidemenu',
  imports: [
    MatSidenavModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatToolbar,
    MatToolbarRow,
    MatIcon,
    MatDivider,
    MatNavList,
    MatListItem,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CommonModule,
  ],
  templateUrl: './sidemenu.component.html',
  styleUrls: ['./sidemenu.component.css', '../../styles/layout.css'],
})
export class SidemenuComponent implements OnInit, AfterViewInit, OnDestroy {
  // Variable que controla si el menú está abierto o cerrado
  opened = true;
  route: string = '/home/dining/general'; // Ruta por defecto

  // Environment variable
  currentEnvironment = environment;
  version: string = '';
  environment: 'Local' | 'Dev' | '' = 'Local';

  // Menú principal de la página
  pageMenu?: ISubMenu;
  originalMenu?: ISubMenu;
  userPermissions: {
    [key: string]: {
      can_read: boolean;
      can_write: boolean;
      can_delete: boolean;
    };
  } = {};

  private routerSubscription: Subscription | undefined;
  private menuRefreshSubscription: Subscription | undefined;

  @ViewChild('sidenav') sidenav!: MatSidenav;

  currentUrl: string = '';

  constructor(
    private router: Router,
    private userService: UsersService,
    private loginService: LoginService,
    private storageService: StorageService,
    private menuRefreshService: MenuRefreshService,
    private tokenExpirationService: TokenExpirationService // Add this injection
  ) {
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.currentUrl = event.urlAfterRedirects;
        this.checkActiveParent();
      });

    // Subscribe to menu refresh events
    this.menuRefreshSubscription =
      this.menuRefreshService.refreshMenu$.subscribe(() => {
        //  console.log('Menu refresh triggered, reloading menu options');
        // Reset userPermissions to ensure clean slate
        this.userPermissions = {};
        // Reset menu completely before reloading
        this.pageMenu = undefined;
        this.originalMenu = undefined;

        // Load fresh permissions and menu
        this.loadUserPermissions();
        this.loadMenuOptions();
      });
  }

  loadUserPermissions() {
    // Clear existing permissions first
    this.userPermissions = {};

    // Check if we're using the new API menu system
    const userSidemenu = this.storageService.getItem<any[]>('user_sidemenu');
    if (userSidemenu && userSidemenu.length > 0) {
      // With the new system, set default route based on menu availability
      const routePriority = [
        { identifier: 'dining_room', route: '/home/dining/general' },
        { identifier: 'communication', route: '/home/communication/mailbox' },
        { identifier: 'company_culture', route: '/home/culture/stories' },
        { identifier: 'users', route: '/home/users/policies' },
      ];

      for (const item of routePriority) {
        const menuItem = userSidemenu.find(
          (menu) => menu.identifier === item.identifier
        );
        if (menuItem) {
          this.route = item.route;
          break;
        }
      }

      // If none of the priority routes are found, use the first available menu item
      if (
        this.route === '/home/dining/general' &&
        !userSidemenu.find((menu) => menu.identifier === 'dining_room')
      ) {
        const firstMenuItem = userSidemenu[0];
        if (firstMenuItem) {
          this.route = this.getRouteFromIdentifier(firstMenuItem.identifier);
        }
      }
      return;
    }

    // Fallback to old permission system for backward compatibility
    const userProfile =
      this.storageService.getItem<UserProfile>('user_profile');

    if (
      userProfile &&
      userProfile.serveto_user_roles &&
      userProfile.serveto_user_roles.length > 0
    ) {
      const role = userProfile.serveto_user_roles[0]; // Using the first role

      if (role.permissions && Array.isArray(role.permissions)) {
        role.permissions.forEach((permission: Permission) => {
          this.userPermissions[permission.functionality] = {
            can_read: permission.can_read,
            can_write: permission.can_write,
            can_delete: permission.can_delete,
          };
        });
      }

      // Logo route based on permissions
      const comedorPermissions = this.userPermissions['Comedor'];
      const comunicacionPermissions = this.userPermissions['Comunicación'];
      const culturaPermissions = this.userPermissions['Cultura'];
      const usuariosPermissions = this.userPermissions['Usuarios'];
      if (comedorPermissions && comedorPermissions.can_read) {
        this.route = '/home/dining/general';
      } else if (comunicacionPermissions && comunicacionPermissions.can_read) {
        this.route = '/home/communication/mailbox';
      } else if (culturaPermissions && culturaPermissions.can_read) {
        this.route = '/home/culture/stories';
      } else if (usuariosPermissions && usuariosPermissions.can_read) {
        this.route = '/home/users/policies';
      }
    }
  }

  // Extract menu loading logic into a separate method
  loadMenuOptions(): void {
    // Get the user sidemenu from storage (loaded from API during login)
    const userSidemenu = this.storageService.getItem<any[]>('user_sidemenu');

    if (userSidemenu && userSidemenu.length > 0) {
      // Convert API menu structure to our internal menu structure
      this.pageMenu = {
        name: 'Menú principal',
        options: this.convertApiMenuToInternalMenu(userSidemenu),
      };

      // Make a copy for originalMenu
      this.originalMenu = JSON.parse(JSON.stringify(this.pageMenu));

      // console.log('Menu loaded from API:', this.pageMenu);
    } else {
      // Fallback to router config if no API menu is available
      this.loadMenuFromRouterConfig();
    }

    this.currentUrl = this.router.url;
    this.checkActiveParent();
  }

  // Fallback method to load menu from router config (for backward compatibility)
  private loadMenuFromRouterConfig(): void {
    const routesWithMenu = this.router.config.filter(
      (route) => route.data?.['menu']
    );

    const mainMenu = routesWithMenu.find(
      (route) => route.data?.['menu'].name === 'Menú principal'
    );

    if (mainMenu) {
      this.originalMenu = JSON.parse(JSON.stringify(mainMenu.data?.['menu']));
      this.pageMenu = JSON.parse(JSON.stringify(mainMenu.data?.['menu']));

      if (this.pageMenu) {
        this.pageMenu.options.forEach((option) => {
          if (
            option.isExpandable &&
            option.subsections &&
            option.subsections.length > 0
          ) {
            option.expanded = false;
          }
        });

        this.filterMenuOptions();
      }
    } else {
      console.error('Menu principal not found in router configuration');
    }
  }

  // Convert API menu structure to internal menu structure
  private convertApiMenuToInternalMenu(apiMenu: any[]): IMenuOption[] {
    return apiMenu.map((item) => {
      // Find the corresponding route configuration to get the icon and not_svg flag
      const routeConfig = this.getRouteConfigByIdentifier(item.identifier);

      const menuOption: IMenuOption = {
        title: item.name,
        link: this.getRouteFromIdentifier(item.identifier),
        identifier: item.identifier,
        icon: routeConfig?.icon || 'circle',
        not_svg: routeConfig?.not_svg,
        isExpandable: item.children && item.children.length > 0,
        expanded: false,
        subsections: item.children
          ? item.children.map((child: any) => ({
              title: child.name,
              link: this.getRouteFromIdentifier(
                child.identifier,
                item.identifier
              ),
              identifier: child.identifier,
            }))
          : undefined,
      };

      return menuOption;
    });
  }

  // Map identifiers to routes
  private getRouteFromIdentifier(
    identifier: string,
    parentIdentifier?: string
  ): string {
    const routeMap: { [key: string]: string } = {
      // Main sections
      users: '/home/users/policies', // Default to policies as it's the first child
      company_culture: '/home/culture/stories', // Default to stories
      communication: '/home/communication/mailbox', // Default to mailbox
      dining_room: '/home/dining/general', // Default to general
      payrolls: '/home/payroll/manage',
      vacations: '/home/vacations/drivers', // Default to drivers
      utilities: '/home/utils',

      // User subsections
      policies: '/home/users/policies',
      users_list: '/home/users/list',

      // Culture subsections
      stories: '/home/culture/stories',
      mission_vision: '/home/culture/mission',
      organizational_chart: '/home/culture/organization',

      // Communication subsections
      inbox: '/home/communication/mailbox',
      incorporations: '/home/communication/incorporation',
      activities: '/home/communication/activities',
      news: '/home/communication/news',

      // Dining subsections
      general: '/home/dining/general',
      prices: '/home/dining/prices',
      kitchen_screen: '/home/dining/cook-screen',

      // Vacation subsections (if needed in future)
      drivers: '/home/vacations/drivers',
      management: '/home/vacations/management',
    };

    return routeMap[identifier] || '#';
  }

  // Get route configuration by identifier from the router config
  private getRouteConfigByIdentifier(
    identifier: string
  ): IMenuOption | undefined {
    const routesWithMenu = this.router.config.filter(
      (route) => route.data?.['menu']
    );

    const mainMenu = routesWithMenu.find(
      (route) => route.data?.['menu'].name === 'Menú principal'
    );

    if (mainMenu && mainMenu.data?.['menu']?.options) {
      return mainMenu.data['menu'].options.find(
        (option: IMenuOption) => option.identifier === identifier
      );
    }

    return undefined;
  }

  toggleSidenav() {
    // Add a small delay before toggling to allow the browser to prepare
    requestAnimationFrame(() => {
      this.opened = !this.opened;

      // Use a slightly shorter timeout to ensure UI is updated before animation ends
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 250);
    });
  }

  toggleSubmenu(option: IMenuOption) {
    // If the menu is collapsed and we click on a parent item, expand the menu first
    if (!this.opened) {
      this.opened = true;
      setTimeout(() => {
        option.expanded = !option.expanded;
      }, 150); // small delay to allow the menu to expand first
    } else {
      option.expanded = !option.expanded;
    }
  }

  // Check if parent should be highlighted based on child route
  isParentActive(option: IMenuOption): boolean {
    if (!option.isExpandable || !option.subsections) return false;

    return option.subsections.some((subsection) =>
      this.currentUrl.includes(subsection.link)
    );
  }

  // Expand parent menu if child is active
  checkActiveParent(): void {
    if (this.pageMenu) {
      this.pageMenu.options.forEach((option) => {
        if (option.isExpandable && option.subsections) {
          const isActive = option.subsections.some((subsection) =>
            this.currentUrl.includes(subsection.link)
          );

          if (isActive) {
            option.expanded = true;
          }
        }
      });
    }
  }

  hasPermission(functionality: string): boolean {
    // Check if we're using the new API menu system
    const userSidemenu = this.storageService.getItem<any[]>('user_sidemenu');
    if (userSidemenu && userSidemenu.length > 0) {
      // With API menu, permissions are already handled server-side
      // Just check if the functionality exists in the user's menu
      return userSidemenu.some((item) => item.name === functionality);
    }

    // Fallback to old permission system for backward compatibility
    const userProfile =
      this.storageService.getItem<UserProfile>('user_profile');
    const isAdmin = userProfile?.is_admin || false;

    // Admin users have access to everything
    if (isAdmin) return true;

    // Check specific permission
    return this.userPermissions[functionality]?.can_read || false;
  }

  filterMenuOptions(): void {
    // This method is now only used for backward compatibility with router config menu
    // When using API menu, the filtering is already done on the server side
    if (!this.originalMenu || !this.pageMenu) {
      return;
    }

    // Create a filtered version of the menu based on permissions
    let filteredOptions = this.originalMenu.options.filter((option) => {
      const functionality = option.title;

      // Always show Vacaciones option regardless of permissions in local environment
      // if (functionality === 'Vacaciones' && this.environment === 'Local') {
      //   return true;
      // }

      const hasPermission = this.hasPermission(functionality);
      return hasPermission;
    });

    // Update the menu
    this.pageMenu.options = filteredOptions;
  }

  ngOnInit(): void {
    this.version = this.currentEnvironment.version;
    if (this.currentEnvironment.local) {
      this.environment = 'Local';
    } else if (this.currentEnvironment.development) {
      this.environment = 'Dev';
    } else {
      this.environment = '';
    }
    // Load user permissions first
    this.loadUserPermissions();
    // Load menu options
    this.loadMenuOptions();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 0);
  }

  ngOnDestroy() {
    // Clean up subscriptions to prevent memory leaks
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.menuRefreshSubscription) {
      this.menuRefreshSubscription.unsubscribe();
    }
  }

  // This method is not being used, can be removed
  sidenavSmall: boolean = false;
  sidenavToggle() {
    this.sidenavSmall = !this.sidenavSmall;
  }

  logout() {
    // Stop token expiration monitoring before logout to prevent race conditions
    this.tokenExpirationService.stopTokenExpirationMonitoring();

    this.loginService.logout();

    this.userService.logout().subscribe({
      next: (response) => {
        // console.log('Response:', response);
        this.router.navigate(['/login']);
      },
      error: (error) => {
        console.error('There was an error!', error);
        // Navigate to login even if there's an error
        this.router.navigate(['/login']);
      },
    });
  }
}
